import { defineStore } from 'pinia';
import _ from 'lodash';

// Stores clipboard data.
// 'multipleChoiceItem' item is used in components/editor/templates/MultipleChoiceInfo.vue
// it is used specially for the MultipleChoiceInfo component.
// Add other items for different contexts if needed. (So user can copy/paste between different contexts.)

/** State of the clipboard module (in-app copy/paste buffers) */
export interface ClipboardState {
  /** Copied multiple-choice item (MultipleChoiceInfo.vue context), or `null` when empty */
  multipleChoiceItem: any;
  /** Deep-cloned copied action content, or `null` when empty */
  actionContent: any;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/clipboard.ts` (namespace
 * `clipboard`). Persisted to `sessionStorage` (full state) — matches the legacy `vuexSession`
 * reducer's `clipboard: state.clipboard` entry in `src/store/index.ts`.
 *
 * No getter/action/mutation name collisions in this module — every mutation/action kept its
 * legacy name.
 */
export const useClipboardStore = defineStore('clipboard', {
  state: (): ClipboardState => ({
    multipleChoiceItem: null,
    actionContent: null,
  }),
  getters: {
    clipboardMultipleChoiceItem: (state: ClipboardState) => state.multipleChoiceItem,
    clipboardAction: (state: ClipboardState) => state.actionContent,
  },
  actions: {
    /**
     * Converted from legacy action `clearMultipleChoiceItem`, which committed the
     * `setMultipleChoiceItem` mutation with a `null` payload. Inlined here since `setMultipleChoiceItem`
     * is kept as its own action below (no name collision to merge).
     */
    clearMultipleChoiceItem() {
      this.setMultipleChoiceItem(null);
    },
    /**
     * Converted from legacy mutation `setMultipleChoiceItem`.
     */
    setMultipleChoiceItem(payload: any) {
      this.multipleChoiceItem = payload;
    },
    /**
     * Converted from legacy mutation `saveAction`.
     */
    saveAction(payload: any) {
      if (!payload) {
        this.actionContent = null;
        return;
      }

      this.actionContent = _.cloneDeep(payload);
    },
  },
  vuexPersist: {
    session: true,
  },
});
